import React, { useEffect, useMemo, useState } from "react";
import "./KycDocuments.css";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";

const API_URL = "https://server.tradefxonline.com/admin_kycdocuments.php";
const APPROVAL_API_URL = "https://server.tradefxonline.com/admin_kycapproval.php";
const BASE_IMG_URL = "https://server.tradefxonline.com/uploads/kyc/";

const KycDocuments = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [kycStatus, setKycStatus] = useState("All");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [actionLoading, setActionLoading] = useState(false);
  const [remarkText, setRemarkText] = useState("");

  // Map numeric Status -> label
  const statusLabel = (code) => {
    if (code === 1 || String(code) === "1") return "Approved";
    if (code === 2 || String(code) === "2") return "Rejected";
    return "Pending";
  };

  const buildImgUrl = (file) =>
    file ? `${BASE_IMG_URL}${file}` : "";

  // Handle KYC approval/rejection
  const handleKycAction = async (kycId, action) => {
   

    try {
      setActionLoading(true);
      const response = await fetch(APPROVAL_API_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          action: action === "approve" ? "approve_kyc" : "reject_kyc",
          kyc_id: kycId,
          admin_remark: action === "approve" ? "Approved" : "Rejected",
        }),
      });

      const result = await response.json();
      
      if (response.ok && result.success) {
        toast.success(`KYC ${action === "approve" ? "approved" : "rejected"} successfully`);
        setRemarkText("");
        fetchKyc(); // Refresh data
      } else {
        toast.error(result.message || `Failed to ${action} KYC`);
      }
    } catch (error) {
      toast.error(`Error: ${error.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  const fetchKyc = async () => {
    try {
      setLoading(true);
      setError("");
      const res = await fetch(API_URL, { headers: { Accept: "application/json" } });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();

      // Handle both array and direct data format
      const list = Array.isArray(json) ? json : (Array.isArray(json?.kyc_documents) ? json.kyc_documents : []);
      
      // Normalize to table fields
      const normalized = list.map((r, i) => ({
        id: r.id ?? i,
        email: r.email || "",
        kyc_type: r.kyc_type || "",
        front_url: buildImgUrl(r.front_image || ""),
        back_url: buildImgUrl(r.back_image || ""),
        address_proof_url: buildImgUrl(r.address_proof || ""),
        address_type: r.address_type || "-",
        status: statusLabel(r.Status),
        admin_remark: r.Admin_Remark || "-",
        registered_at: r.registered_date_js || "",
        trade_id: r.trade_id || "",
      }));

      setRows(normalized);
    } catch (e) {
      setRows([]);
      setError(e?.message || "Failed to load KYC documents");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchKyc();
  }, []);

  // Client-side filtering and sorting to avoid backend coupling
  const filtered = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    const wantStatus = kycStatus.toLowerCase();

    const filteredRows = rows.filter((r) => {
      // status filter
      if (wantStatus !== "all" && r.status.toLowerCase() !== wantStatus) return false;

      // date filter (registered_at is "YYYY-MM-DD HH:mm:ss")
      if (startDate) {
        const d = (r.registered_at || "").slice(0, 10);
        if (d && d < startDate) return false;
      }
      if (endDate) {
        const d = (r.registered_at || "").slice(0, 10);
        if (d && d > endDate) return false;
      }

      // search filter across common fields
      if (!q) return true;
      const hay = [
        r.email,
        r.trade_id,
        r.kyc_type,
        r.address_type,
        r.admin_remark,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return hay.includes(q);
    });

    // Sort by registered_at in descending order (most recent dates first)
      return filteredRows.sort((a, b) => {
        const dateA = new Date(a.registered_at || "1970-01-01");
        const dateB = new Date(b.registered_at || "1970-01-01");
        return dateB - dateA;
      });
   }, [rows, searchTerm, kycStatus, startDate, endDate]);

  const linkOrDash = (url) =>
    url ? (
      <a href={url} target="_blank" rel="noreferrer">
        View
      </a>
    ) : (
      "-"
    );

  // Export to Excel function
  const exportToExcel = () => {
    if (filtered.length === 0) {
      toast.warning("No data to export");
      return;
    }

    // Prepare data for export
    const exportData = filtered.map((r, idx) => ({
      "#": idx + 1,
      "Email": r.email,
      "KYC Type": r.kyc_type,
      "Front Side URL": r.front_url || "-",
      "Back Side URL": r.back_url || "-",
      "Address Proof URL": r.address_proof_url || "-",
      "Address Type": r.address_type,
      "Status": r.status,
      "Registered Date": r.registered_at,
      "Admin Remark": r.admin_remark,
    }));

    // Create workbook and worksheet
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(exportData);

    // Set column widths
    ws["!cols"] = [
      { wch: 5 },   // #
      { wch: 30 },  // Email
      { wch: 15 },  // KYC Type
      { wch: 50 },  // Front Side URL
      { wch: 50 },  // Back Side URL
      { wch: 50 },  // Address Proof URL
      { wch: 15 },  // Address Type
      { wch: 12 },  // Status
      { wch: 20 },  // Registered Date
      { wch: 25 },  // Admin Remark
    ];

    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(wb, ws, "KYC Documents");

    // Generate filename with current date
    const date = new Date().toISOString().slice(0, 10);
    const filename = `KYC_Documents_${date}.xlsx`;

    // Download file
    XLSX.writeFile(wb, filename);
    toast.success("Excel file downloaded successfully!");
  };

  return (
    <div className="kyc-documents-container">
      <div className="header-section">
        <h1>KYC Documents</h1>
        <div className="header-actions">
          <button 
            className="export-btn" 
            onClick={exportToExcel}
            style={{
              backgroundColor: '#28a745',
              color: 'white',
              border: 'none',
              padding: '10px 20px',
              borderRadius: '5px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <i className="fas fa-file-excel"></i> Export to Excel
          </button>
        </div>
        <div className="breadcrumb">
          Home / <span>View All</span>
        </div>
      </div>

      <div className="filters-section">
        <div className="filter-row">
          <div className="filter-group">
            <label>Search</label>
            <input
              type="text"
              placeholder="Client Name, Email, Card or Address"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="filter-group">
            <label>Filter by Kyc Status</label>
            <select value={kycStatus} onChange={(e) => setKycStatus(e.target.value)}>
              <option value="All">All</option>
              <option value="Pending">Pending</option>
              <option value="Approved">Approved</option>
              <option value="Rejected">Rejected</option>
            </select>
          </div>
          <div className="filter-group">
            <label>Start Date</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </div>
          <div className="filter-group">
            <label>End Date</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>
        </div>
      </div>

      <div className="admin-remark-section">
        <div className="remark-input-group">
          <label htmlFor="adminRemark">Admin Remark (Required for Approval/Rejection):</label>
          <textarea
            id="adminRemark"
            value={remarkText}
            onChange={(e) => setRemarkText(e.target.value)}
            placeholder="Enter admin remark here..."
            rows="3"
            className="remark-textarea"
          />
        </div>
      </div>

      <div className="table-container">
        <table className="kyc-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Email</th>
              <th>KYC Type</th>
              <th>Front Side</th>
              <th>Back Side</th>
              <th>Address Proof</th>
              <th>Address Type</th>
              <th>Status</th>
              <th>Registered Date</th>
              <th>Admin Remark</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="11" className="no-data">Loading…</td>
              </tr>
            ) : error ? (
              <tr>
                <td colSpan="11" className="no-data" style={{ color: "crimson" }}>
                  Error: {error}
                </td>
              </tr>
            ) : filtered.length === 0 ? (
              <tr>
                <td colSpan="11" className="no-data">No data available in table</td>
              </tr>
            ) : (
              filtered.map((r, idx) => (
                <tr key={r.id ?? idx}>
                  <td>{idx + 1}</td>
                  <td>{r.email}</td>
                  <td>{r.kyc_type}</td>
                  <td>{linkOrDash(r.front_url)}</td>
                  <td>{linkOrDash(r.back_url)}</td>
                  <td>{linkOrDash(r.address_proof_url)}</td>
                  <td>{r.address_type}</td>
                  <td>
                    <span className={`status-badge ${
                      r.status === 'Approved' ? 'approved' : 
                      r.status === 'Rejected' ? 'rejected' : 'pending'
                    }`}>
                      {r.status}
                    </span>
                  </td>
                  <td>{r.registered_at}</td>
                  <td>{r.admin_remark}</td>
                  <td>
                    {r.status === 'Pending' ? (
                      <div className="action-buttons">
                        <button
                          onClick={() => handleKycAction(r.id, 'approve')}
                          disabled={actionLoading}
                          className="btn-approve"
                        >
                          Approve
                        </button>
                        <button
                          onClick={() => handleKycAction(r.id, 'reject')}
                          disabled={actionLoading}
                          className="btn-reject"
                        >
                          Reject
                        </button>
                      </div>
                    ) : (
                      <span>-</span>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <footer className="footer">
        <p>
          Copyright © 2025 <span className="brand">Ample Markets</span>. All
          rights reserved.
        </p>
        <span className="version">Version 2.0</span>
      </footer>
    </div>
  );
};

export default KycDocuments;
